import { MaybeAsync } from '@angular/router';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

export namespace GuardHelpers {

  export const ROUTE_GUARD_LOGGING_ENABLED_ENV = 'routeGuardLoggingEnabled';

  /**
   * Can be set in environment file using the field name defined at {@link ROUTE_GUARD_LOGGING_ENABLED_ENV}.
   */
  export function isRouteGuardLoggingEnabled(): boolean {
    return !!environment[ROUTE_GUARD_LOGGING_ENABLED_ENV];
  }

  /**
   * Logs the value of a value that might be wrapped in an observable or promise, with a message.
   * For Observable or Promises the value stays wrapped, otherwise the raw value is returned.
   *
   * The following is the same as `console.log('ABC', 123)`:
   * ```
   * GuardHelpers.logMaybeAsync('ABC', of(123));
   * ```
   */
  export function logMaybeAsync<T>(message: string, value: MaybeAsync<T>): MaybeAsync<T> {
    if (!isRouteGuardLoggingEnabled()) {
      // skip checking for types since we can't log
      return value;
    }
    if (value instanceof Observable) {
      return value.pipe(tap(v => console.debug(message, v)));
    }
    if (value instanceof Promise) {
      return value.then((v) => {
        console.debug(message, v);
        return v;
      });
    }
    console.debug(message, value);
    return value;
  }

  /**
   * Should match any Angular Functional Guard.
   */
  export type AnyGuardFn<R> = (...args: any[]) => MaybeAsync<R>

  /**
   * Wraps the given guard with logs that help trace route guard execution.
   *
   * The following example will print `GUARD START myGuard` at the start, and then `GUARD END myGuard false` when a$ is
   * missing. When a$ is present and a.hasFlag is true, it will print `GUARD END myGuard true`.
   *
   * Example:
   * ```
   * const myGuard: CanActivateFn = GuardHelpers.wrapWithLogs('myGuard', (route, snapshot) => {
   *  if (a$ == null) {
   *    return false;
   *  }
   *  return a$.pipe(
   *    map(a => a.hasFlag())
   *  );
   * });
   * ```
   */
  export function wrapWithLogs<R, T extends AnyGuardFn<R>>(guardName: string, guard: T): T {
    const wrapped = (...args: any[]) => {
      if (isRouteGuardLoggingEnabled()) {
        console.debug('GUARD START', guardName);
      }
      const result: MaybeAsync<R> = guard.apply(this, args);
      return logMaybeAsync(`GUARD END ${guardName}`, result);
    };
    return wrapped as any;
  }

}
